import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from './config';
import Header from './components/Header';
// Importar componentes administrativos
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import { UserAuthProvider, useUserAuth } from './contexts/UserAuthContext';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AuthScreen from './components/AuthScreen';
import ProfileScreen from './components/ProfileScreen';
// Importar componentes separados
import PWAInstallPrompt from './components/PWAInstallPrompt';
import FloatingActionButton from './components/FloatingActionButton';
import Content from './components/Content';
import NewPrayer from './components/NewPrayer';
import Dashboard from './components/Dashboard';
import PrayerDetail from './components/PrayerDetail';
import Unsubscribe from './components/Unsubscribe';
import UnsubscribeAll from './components/UnsubscribeAll';
import QuickPrayerInput from './components/QuickPrayerInput';
import InfoPanel from './components/InfoPanel';
import './App.css';

// Componente para rutas protegidas
function ProtectedAdminRoute({ children }) {
  const { isAuthenticated, loading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Redirigir al login pero recordar la ubicación intentada
      navigate('/admin', { state: { from: location } });
    }
  }, [isAuthenticated, loading, navigate, location]);

  if (loading) {
    return (
      <div className="loading">
        <h2>Verificando autenticación...</h2>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // El useEffect se encargará de la redirección
  }

  return children;
}

// Componente de página admin que maneja login/dashboard
function AdminPage() {
  const { isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLoginSuccess = () => {
    // Redirigir a donde el usuario quería ir originalmente, o al dashboard
    const from = location.state?.from?.pathname || '/admin/dashboard';
    navigate(from);
  };

  if (isAuthenticated) {
    // Si ya está autenticado y está en /admin, redirigir al dashboard
    navigate('/admin/dashboard');
    return null;
  }

  return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
}

// Frases bíblicas sobre la oración (Biblia católica)
const PRAYER_QUOTES = [
  { text: 'Pedid y se os dará; buscad y hallaréis; llamad y se os abrirá.', ref: 'Mateo 7, 7' },
  { text: 'El Señor está cerca de los que lo invocan, de los que lo invocan con sinceridad.', ref: 'Salmo 145, 18' },
  { text: 'Orad sin cesar.', ref: '1 Tesalonicenses 5, 17' },
  { text: 'Confía en el Señor con todo tu corazón y no te apoyes en tu propia inteligencia.', ref: 'Proverbios 3, 5' },
  { text: 'Venid a mí todos los que estáis cansados y agobiados, y yo os aliviaré.', ref: 'Mateo 11, 28' },
  { text: 'No se inquieten por nada; más bien, en toda ocasión, presenten sus peticiones a Dios.', ref: 'Filipenses 4, 6' },
  { text: 'Porque donde hay dos o tres reunidos en mi nombre, allí estoy yo en medio de ellos.', ref: 'Mateo 18, 20' },
  { text: 'El Señor es mi pastor, nada me falta.', ref: 'Salmo 23, 1' },
  { text: 'Clama a mí y te responderé; te daré a conocer cosas grandes y ocultas que tú no sabes.', ref: 'Jeremías 33, 3' },
  { text: 'Todo lo puedo en aquel que me fortalece.', ref: 'Filipenses 4, 13' },
];

// Componente de pantalla de carga
function LoadingScreen() {
  const [quoteIndex, setQuoteIndex] = useState(() =>
    Math.floor(Math.random() * PRAYER_QUOTES.length)
  );
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setQuoteIndex((prev) => {
          let next;
          do {
            next = Math.floor(Math.random() * PRAYER_QUOTES.length);
          } while (next === prev && PRAYER_QUOTES.length > 1);
          return next;
        });
        setFading(false);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const quote = PRAYER_QUOTES[quoteIndex];

  return (
    <div className="loading">
      <div className="loading-quote-container">
        <div className={`loading-quote ${fading ? 'fade-out' : 'fade-in'}`}>
          <p className="loading-quote-text">"{quote.text}"</p>
          <span className="loading-quote-ref">— {quote.ref}</span>
        </div>
      </div>
      <div className="loading-spinner-large"></div>
      <p className="loading-subtitle">Cargando pedidos de oración...</p>
    </div>
  );
}

// Funciones para manejar oraciones diarias
const markUserPrayedToday = (prayerId) => {
  const key = `prayed_${prayerId}_${new Date().toDateString()}`;
  localStorage.setItem(key, 'true');
};

const hasUserPrayedToday = (prayerId) => {
  const key = `prayed_${prayerId}_${new Date().toDateString()}`;
  return localStorage.getItem(key) === 'true';
};

const cleanOldPrayerData = () => {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('prayed_') && /^prayed_\d+_/.test(key)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

const PRAYERS_PAGE_SIZE = 20;

// Componente principal de la app (sin las rutas admin)
function MainApp() {
  const location = useLocation();
  const { token: userToken, isAuthenticated: isUserAuth, user } = useUserAuth();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    salud: 0,
    paz: 0,
    otros: 0
  });
  const [activeInfoTab, setActiveInfoTab] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadOlderError, setLoadOlderError] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const apiUrl = `${config.API_BASE_URL}/api/prayers/stats`;
      const response = await axios.get(apiUrl);
      const data = response.data || {};
      setStats({
        total: Number(data.total) || 0,
        salud: Number(data.salud) || 0,
        paz: Number(data.paz) || 0,
        otros: Number(data.otros) || 0
      });
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  }, []);

  const fetchInitialPrayers = useCallback(async () => {
    try {
      const apiUrl = `${config.API_BASE_URL}/api/prayers`;
      const response = await axios.get(apiUrl, {
        params: { limit: PRAYERS_PAGE_SIZE }
      });
      const payload = response.data || {};
      const list = Array.isArray(payload.prayers) ? payload.prayers : [];
      setPrayers(list);
      setHasMore(Boolean(payload.hasMore));
      setNextCursor(payload.nextCursor ?? null);
      setLoadOlderError(false);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar pedidos:', error);
      setLoading(false);
    }
  }, []);

  const fetchOlderPrayers = useCallback(async () => {
    if (!hasMore || loadingOlder || nextCursor == null) return;
    setLoadingOlder(true);
    setLoadOlderError(false);
    try {
      const apiUrl = `${config.API_BASE_URL}/api/prayers`;
      const response = await axios.get(apiUrl, {
        params: { limit: PRAYERS_PAGE_SIZE, before: nextCursor }
      });
      const payload = response.data || {};
      const older = Array.isArray(payload.prayers) ? payload.prayers : [];
      setPrayers((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const deduped = older.filter((p) => !existingIds.has(p.id));
        return [...prev, ...deduped];
      });
      setHasMore(Boolean(payload.hasMore));
      setNextCursor(payload.nextCursor ?? null);
    } catch (error) {
      console.error('Error al cargar pedidos anteriores:', error);
      setLoadOlderError(true);
    } finally {
      setLoadingOlder(false);
    }
  }, [hasMore, loadingOlder, nextCursor]);

  const retryLoadOlder = useCallback(() => {
    setLoadOlderError(false);
    fetchOlderPrayers();
  }, [fetchOlderPrayers]);

  useEffect(() => {
    cleanOldPrayerData();
    fetchInitialPrayers();
    fetchStats();
  }, [fetchInitialPrayers, fetchStats]);

  const handleNewPrayer = async (prayerData) => {
    // --- Optimistic update ---------------------------------------------------
    // Creamos un pedido temporal con los datos disponibles localmente y lo
    // insertamos en el feed ANTES de la llamada al servidor. El usuario ve
    // inmediatamente su pedido publicado. Luego reconciliamos con el resultado
    // real (reemplazamos el temporal por el que devuelve el backend) o
    // hacemos rollback (lo eliminamos) si la llamada falla.
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const fechaTemp = `${year}-${month}-${day}`;

    // Autenticado: tomamos nombre/email/pais del contexto. No autenticado
    // (wizard): vienen en prayerData.
    const authUser = isUserAuth && user ? user : null;
    const tempPrayer = {
      id: tempId,
      email: authUser?.email || prayerData.email || '',
      nombre: authUser?.nombre || prayerData.nombre || '',
      pais: authUser?.pais || prayerData.pais || '',
      comentario: prayerData.comentario,
      fecha: fechaTemp,
      categoria: '',
      oraciones: 0,
      visible: prayerData.visible !== false,
      isPending: true,
    };

    setPrayers((prev) => [tempPrayer, ...prev]);

    // Scroll suave al final para que el usuario vea su pedido recien publicado.
    // Doble rAF garantiza que React haya pintado el nuevo card antes de medir.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollingEl = document.scrollingElement || document.documentElement;
        scrollingEl.scrollTo({
          top: scrollingEl.scrollHeight,
          behavior: 'smooth',
        });
      });
    });

    try {
      const apiUrl = `${config.API_BASE_URL}/api/prayers`;
      const headers = {};
      if (isUserAuth && userToken) {
        headers['Authorization'] = `Bearer ${userToken}`;
      }
      const response = await axios.post(apiUrl, prayerData, { headers });

      if (response.data.success) {
        const newPrayer = response.data.data;
        // Reconcile: reemplazamos el temporal por el real manteniendo posicion.
        setPrayers((prev) => {
          const withoutTemp = prev.filter((p) => p.id !== tempId);
          if (
            newPrayer &&
            newPrayer.id != null &&
            !withoutTemp.some((p) => p.id === newPrayer.id)
          ) {
            return [newPrayer, ...withoutTemp];
          }
          return withoutTemp;
        });

        fetchStats();

        return {
          success: true,
          data: response.data.data,
          isReturningUser: response.data.isReturningUser || false,
          userStats: response.data.userStats || {},
          hasActiveSubscription: response.data.hasActiveSubscription || false,
          subscriptionPreferences: response.data.subscriptionPreferences || null
        };
      } else {
        throw new Error('Error al enviar pedido');
      }
    } catch (error) {
      console.error('Error al enviar pedido:', error);
      // Rollback: quitamos el pedido temporal del feed.
      setPrayers((prev) => prev.filter((p) => p.id !== tempId));
      // Propagamos el error al caller (QuickPrayerInput) para que muestre
      // el toast y restaure el texto en el textarea.
      throw error;
    }
  };

  const handlePrayForThis = async (prayerId) => {
    if (hasUserPrayedToday(prayerId)) {
      alert('Ya oraste por esta intención hoy. ¡Puedes orar de nuevo mañana!');
      return;
    }

    // ✨ OPTIMISTIC UPDATE: Incrementar contador INMEDIATAMENTE
    setPrayers(prevPrayers => 
      prevPrayers.map(prayer => 
        prayer.id === prayerId 
          ? { ...prayer, oraciones: (prayer.oraciones || 0) + 1 }
          : prayer
      )
    );

    try {
      const apiUrl = `${config.API_BASE_URL}/api/prayers`;
      const response = await axios.patch(`${apiUrl}/${prayerId}/pray`);
      if (response.data.success) {
        // Actualizar con el valor real del servidor
        setPrayers(prevPrayers => 
          prevPrayers.map(prayer => 
            prayer.id === prayerId 
              ? { ...prayer, oraciones: response.data.oraciones }
              : prayer
          )
        );
        
        setTimeout(() => {
          markUserPrayedToday(prayerId);
          setPrayers(prevPrayers => [...prevPrayers]);
        }, 1500);
      }
    } catch (error) {
      console.error('Error al rezar por este pedido:', error);
      
      // 🔄 ROLLBACK: Si falla, revertir el incremento optimista
      setPrayers(prevPrayers => 
        prevPrayers.map(prayer => 
          prayer.id === prayerId 
            ? { ...prayer, oraciones: Math.max(0, (prayer.oraciones || 0) - 1) }
            : prayer
        )
      );
      
      alert('Error al registrar tu oración. Inténtalo de nuevo.');
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="app">
      <PWAInstallPrompt />
      <Header activeInfoTab={activeInfoTab} onInfoTabClick={setActiveInfoTab} />
      {location.pathname === '/' && (
        <div className="info-tabs-wrapper">
          <button
            className={`header-info-tab ${activeInfoTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveInfoTab(activeInfoTab === 'events' ? null : 'events')}
          >
            <span className="header-info-tab-icon">📅</span>
            <span className="header-info-tab-label">Eventos</span>
          </button>
          <button
            className={`header-info-tab ${activeInfoTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveInfoTab(activeInfoTab === 'stats' ? null : 'stats')}
          >
            <span className="header-info-tab-icon">📊</span>
            <span className="header-info-tab-label">{stats.total} Pedidos</span>
          </button>
        </div>
      )}
      <InfoPanel stats={stats} activeTab={activeInfoTab} onClose={() => setActiveInfoTab(null)} />

      <main className="main-content">
        <Routes>
          <Route 
            path="/" 
            element={
              <Dashboard
                prayers={prayers}
                stats={stats}
                onPrayForThis={handlePrayForThis}
                hasMore={hasMore}
                loadingOlder={loadingOlder}
                loadOlderError={loadOlderError}
                onLoadOlder={fetchOlderPrayers}
                onRetryLoadOlder={retryLoadOlder}
              />
            }
          />
          <Route 
            path="/nuevo-pedido" 
            element={<NewPrayer onSubmit={handleNewPrayer} />} 
          />
          <Route 
            path="/contenido" 
            element={<Content />} 
          />
          <Route 
            path="/login" 
            element={<AuthScreen />} 
          />
          <Route 
            path="/auth/verify" 
            element={<AuthScreen />} 
          />
          <Route 
            path="/perfil" 
            element={<ProfileScreen />} 
          />
          <Route 
            path="/oracion/:id" 
            element={<PrayerDetail />} 
          />
          <Route 
            path="/desuscribir/:token" 
            element={<Unsubscribe />} 
          />
          <Route 
            path="/desuscribir-todo/:email" 
            element={<UnsubscribeAll />} 
          />
        </Routes>
      </main>

      {/* Barra rápida de pedido (solo en Dashboard) */}
      {location.pathname === '/' && (
        <QuickPrayerInput onSubmit={handleNewPrayer} />
      )}

      {/* Botón flotante para nuevo pedido */}
      <FloatingActionButton />
    </div>
  );
}

// Componente raíz que maneja todas las rutas
function App() {
  return (
    <UserAuthProvider>
      <AdminAuthProvider>
        <Routes>
          {/* Rutas administrativas */}
          <Route path="/admin" element={<AdminPage />} />
          <Route 
            path="/admin/dashboard" 
            element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            } 
          />
          
          {/* Rutas principales de la aplicación */}
          <Route path="/*" element={<MainApp />} />
        </Routes>
      </AdminAuthProvider>
    </UserAuthProvider>
  );
}

export default App;